import {
  MediaUploadResponseSchema,
  PresignedUrlResponseSchema,
  type MediaUploadResponse,
  type PresignedUrlRequest,
  type PresignedUrlResponse,
  type SetAvatarRequest,
  type SetLogoRequest,
} from '@lance-x/shared';
import type { ApiClient } from '../index';

/**
 * Phase 11 (MEDIA-01..03) — typed wrapper over the avatar/logo upload surface.
 *
 * Three methods cover the full two-step flow on the API side:
 *  - requestPresignedUrl: ask for a signed PUT URL.
 *  - setAvatar / setLogo: post-upload finalize (server downloads + Sharp + persists).
 *
 * The raw PUT to R2 happens directly from the browser/native client, NOT through
 * this api-client — see Pattern 6 in 11-RESEARCH.md and the mobile component
 * shipped in Plan 11-02.
 */
export class MediaEndpoints {
  constructor(private readonly client: ApiClient) {}

  /**
   * POST /media/presigned-url
   * @returns short-lived (60s) signed PUT URL + canonical raw key.
   */
  requestPresignedUrl(input: PresignedUrlRequest): Promise<PresignedUrlResponse> {
    return this.client.post('/media/presigned-url', input, {
      responseSchema: PresignedUrlResponseSchema,
    });
  }

  /**
   * POST /profissionais/me/avatar
   * @returns the canonical public URL of the processed avatar.
   */
  setAvatar(input: SetAvatarRequest): Promise<MediaUploadResponse> {
    return this.client.post('/profissionais/me/avatar', input, {
      responseSchema: MediaUploadResponseSchema,
    });
  }

  /**
   * POST /empresas/{empresaId}/logo
   * @returns the canonical public URL of the processed logo.
   */
  setLogo(
    empresaId: string,
    input: SetLogoRequest,
  ): Promise<MediaUploadResponse> {
    return this.client.post(`/empresas/${empresaId}/logo`, input, {
      responseSchema: MediaUploadResponseSchema,
    });
  }
}
